import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import Layout from "@/components/layout/Layout";
import Dashboard from "@/pages/Dashboard";
import NewRentalPage from "@/pages/NewRentalPage";
import RentalsListPage from "@/pages/RentalsListPage";
import RentalDetailPage from "@/pages/RentalDetailPage";
import FleetPage from "@/pages/FleetPage";
import ReturnsPage from "@/pages/ReturnsPage";
import AgreementsPage from "@/pages/AgreementsPage";
import AgreementSignPage from "@/pages/AgreementSignPage";
import AdminSettings from "@/pages/AdminSettings";
import ProfilePage from "@/pages/ProfilePage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import OrgSetupPage from "@/pages/OrgSetupPage";
import TeamPage from "@/pages/TeamPage";
import BillingPage from "@/pages/BillingPage";
import NotFoundPage from "@/pages/NotFoundPage";
import { fetchUsers, fetchOrganizations, fetchBillingInfo } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-sm text-slate-500 font-medium">Loading...</div>
      </div>
    );
  }
  if (user === false) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function OrgGuard({ children }) {
  const { user } = useAuth();
  const [checking, setChecking] = useState(true);
  const [hasOrg, setHasOrg] = useState(false);

  useEffect(() => {
    if (!user || user === false) { setChecking(false); return; }
    fetchOrganizations().then(orgs => {
      setHasOrg(orgs.length > 0);
      setChecking(false);
    }).catch(() => setChecking(false));
  }, [user]);

  if (checking) return <div className="h-screen w-full flex items-center justify-center bg-[#F8FAFC]"><div className="text-sm text-slate-500 font-medium">Loading...</div></div>;
  if (!hasOrg) return <Navigate to="/setup" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user && user._org_role && user._org_role === "STAFF") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppContent() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [billingInfo, setBillingInfo] = useState(null);

  const loadGlobalData = useCallback(async () => {
    if (!user || user === false) return;
    try {
      const [u, b] = await Promise.all([fetchUsers(), fetchBillingInfo().catch(() => null)]);
      setUsers(u);
      setBillingInfo(b);
    } catch (e) {
      console.error("Failed to load global data", e);
    }
  }, [user]);

  useEffect(() => { loadGlobalData(); }, [loadGlobalData]);

  function AppFrame({ children }) {
    return (
      <ProtectedRoute>
        <OrgGuard>
          <Layout currentUser={user} billingInfo={billingInfo}>
            {children}
          </Layout>
        </OrgGuard>
      </ProtectedRoute>
    );
  }

  function AppShell() {
    return (
      <AppFrame>
        <Outlet />
      </AppFrame>
    );
  }

  function UnknownRoute() {
    if (user && user !== false) {
      return (
        <AppFrame>
          <NotFoundPage inApp />
        </AppFrame>
      );
    }
    return <NotFoundPage />;
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to={user && user !== false ? "/dashboard" : "/login"} replace />} />
      <Route path="/login" element={user && user !== false ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/signup" element={user && user !== false ? <Navigate to="/dashboard" replace /> : <SignupPage />} />
      <Route path="/forgot-password" element={user && user !== false ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/sign/:token" element={<AgreementSignPage />} />
      <Route path="/setup" element={<ProtectedRoute><OrgSetupPage /></ProtectedRoute>} />
      <Route element={<AppShell />}>
        <Route path="/dashboard" element={<Dashboard users={users} />} />
        <Route path="/rentals/new" element={<NewRentalPage users={users} currentUser={user} />} />
        <Route path="/rentals" element={<RentalsListPage />} />
        <Route path="/rentals/:rentalId" element={<RentalDetailPage users={users} currentUser={user} />} />
        <Route path="/fleet" element={<FleetPage />} />
        <Route path="/returns" element={<ReturnsPage />} />
        <Route path="/agreements" element={<AgreementsPage />} />
        <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
        <Route path="/team" element={<AdminRoute><TeamPage /></AdminRoute>} />
        <Route path="/billing" element={<AdminRoute><BillingPage /></AdminRoute>} />
        <Route path="/profile" element={<ProfilePage currentUser={user} users={users} />} />
      </Route>
      <Route path="*" element={<UnknownRoute />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
